import React from 'react';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import Breadcrumb from '../components/Breadcrumb';
import { useBreadcrumb } from '../contexts/BreadcrumbContext';

interface Collection {
  id: string;
  name: string;
  description: string;
  image_url: string;
  slug: string;
}

export default function Collections() {
  const [collections, setCollections] = React.useState<Collection[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const { setItems } = useBreadcrumb();

  React.useEffect(() => {
    setItems([
      { label: 'Home', path: '/' },
      { label: 'Collections', path: '/collections' }
    ]);
  }, [setItems]);

  React.useEffect(() => {
    async function fetchCollections() {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('collections')
          .select('*')
          .order('name');

        if (error) throw error;
        setCollections(data || []);
      } catch (err) {
        console.error('Error fetching collections:', err);
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    }

    fetchCollections();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <Breadcrumb />
        
        <h1 className="text-3xl font-bold mb-8">Garden Collections</h1>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
          </div>
        ) : error ? (
          <div className="text-red-600">{error}</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {collections.map((collection) => (
              <Link
                key={collection.id}
                to={`/collections/${collection.slug}`}
                className="group relative overflow-hidden rounded-lg aspect-[4/3]"
              >
                <img
                  src={collection.image_url}
                  alt={collection.name}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-black/20" />
                <div className="absolute inset-0 p-6 flex flex-col justify-end">
                  <h3 className="text-white font-semibold text-xl mb-2">
                    {collection.name}
                  </h3>
                  <p className="text-gray-200 text-sm line-clamp-2">
                    {collection.description}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}